import { useEffect, useMemo, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { ScheduleTeam, ScheduleUser } from './utils'

interface Props {
  token: string
  clientId: number
  users: ScheduleUser[]
  onClose: () => void
  onChanged: () => void
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b']

export default function TeamsModal({ token, clientId, users, onClose, onChanged }: Props) {
  const { showError, showSuccess } = useNotificationContext()
  const [teams, setTeams] = useState<ScheduleTeam[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Partial<ScheduleTeam> | null>(null)
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [memberFilter, setMemberFilter] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/schedule/teams?client_id=${clientId}&include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setTeams(await r.json())
      else showError(`Nie udało się pobrać zespołów: ${await r.text()}`)
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() /* eslint-disable-next-line */ }, [clientId])

  const startNew = () => {
    setEditing({
      client_id: clientId,
      name: '',
      description: '',
      color: '#3b82f6',
      is_active: true,
    })
    setMemberIds([])
    setMemberFilter('')
  }
  const startEdit = (t: ScheduleTeam) => {
    setEditing({ ...t })
    setMemberIds(t.members.map(m => m.id))
    setMemberFilter('')
  }
  const cancelEdit = () => {
    setEditing(null)
    setMemberIds([])
  }

  const toggleMember = (id: number) => {
    setMemberIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const filteredUsers = useMemo(() => {
    if (!memberFilter.trim()) return users
    const q = memberFilter.toLowerCase()
    return users.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q)
    )
  }, [users, memberFilter])

  const handleSave = async () => {
    if (!editing) return
    if (!editing.name?.trim()) {
      showError('Podaj nazwę zespołu')
      return
    }
    setBusy(true)
    try {
      const body: any = {
        client_id: clientId,
        name: editing.name?.trim(),
        description: editing.description || null,
        color: editing.color || null,
        is_active: editing.is_active !== false,
        member_ids: memberIds,
      }
      const url = editing.id
        ? `/api/schedule/teams/${editing.id}`
        : `/api/schedule/teams`
      const method = editing.id ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(await r.text())
      showSuccess(editing.id ? 'Zespół zaktualizowany' : 'Zespół utworzony')
      setEditing(null)
      setMemberIds([])
      await reload()
      onChanged()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (t: ScheduleTeam, hard: boolean) => {
    const msg = hard
      ? `Usunąć trwale zespół "${t.name}"?`
      : `Dezaktywować zespół "${t.name}"?`
    if (!window.confirm(msg)) return
    setBusy(true)
    try {
      const r = await fetch(`/api/schedule/teams/${t.id}?hard=${hard}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok && r.status !== 204) throw new Error(await r.text())
      showSuccess('Wykonano')
      await reload()
      onChanged()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">👥 Zespoły pracowników</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="flex-1 overflow-auto">
          {!editing && (
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Zespoły to grupy pracowników, które można szybko wybrać przy masowym przypisywaniu w grafiku.
                </p>
                <button onClick={startNew} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                  + Nowy zespół
                </button>
              </div>
              {loading && <div className="text-sm text-gray-500">Ładowanie…</div>}
              {!loading && teams.length === 0 && (
                <p className="text-sm text-gray-500 italic">Brak zespołów. Utwórz pierwszy.</p>
              )}
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
                {teams.map(t => (
                  <li key={t.id} className="p-2 flex items-start gap-3">
                    <span
                      className="inline-block w-4 h-4 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: t.color || '#94a3b8' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                        {t.name}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                          {t.member_count} {t.member_count === 1 ? 'osoba' : 'osób'}
                        </span>
                        {!t.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                            nieaktywny
                          </span>
                        )}
                      </div>
                      {t.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                      )}
                      {t.members.length > 0 && (
                        <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-1">
                          {t.members.slice(0, 8).map(m => (
                            <span key={m.id} className="px-1.5 py-0.5 bg-gray-100 rounded">
                              {m.full_name || m.username}
                            </span>
                          ))}
                          {t.members.length > 8 && (
                            <span className="px-1.5 py-0.5 text-gray-500">+{t.members.length - 8}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <button
                        onClick={() => startEdit(t)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDelete(t, false)}
                        className="text-xs text-gray-500 hover:underline"
                        title="Dezaktywuj"
                      >
                        Dezaktywuj
                      </button>
                      <button
                        onClick={() => handleDelete(t, true)}
                        className="text-xs text-red-600 hover:underline"
                        title="Usuń trwale"
                      >
                        Usuń
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {editing && (
            <div className="p-4 space-y-3">
              <h3 className="font-semibold">{editing.id ? 'Edycja zespołu' : 'Nowy zespół'}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nazwa zespołu">
                  <input
                    type="text"
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="np. Brygada A, Dyspozytornia, Konserwatorzy"
                    value={editing.name || ''}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                  />
                </Field>
                <Field label="Aktywny">
                  <label className="inline-flex items-center text-sm">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={editing.is_active !== false}
                      onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                    />
                    Widoczny w listach
                  </label>
                </Field>
                <Field label="Kolor (chip)" className="sm:col-span-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-9 w-12 border border-gray-300 rounded"
                      value={editing.color || '#3b82f6'}
                      onChange={e => setEditing({ ...editing, color: e.target.value })}
                    />
                    <div className="flex gap-1">
                      {DEFAULT_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: c }}
                          onClick={() => setEditing({ ...editing, color: c })}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                </Field>
                <Field label="Opis (opcjonalnie)" className="sm:col-span-2">
                  <textarea
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    rows={2}
                    value={editing.description || ''}
                    onChange={e => setEditing({ ...editing, description: e.target.value })}
                  />
                </Field>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Członkowie zespołu ({memberIds.length}/{users.length})
                  </span>
                  <div className="text-xs">
                    <button onClick={() => setMemberIds(users.map(u => u.id))} className="text-blue-600 hover:underline">
                      zaznacz wszystkich
                    </button>
                    <span className="mx-1 text-gray-400">·</span>
                    <button onClick={() => setMemberIds([])} className="text-blue-600 hover:underline">
                      odznacz
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm mb-2 text-sm"
                  placeholder="Filtruj po imieniu / loginie…"
                  value={memberFilter}
                  onChange={e => setMemberFilter(e.target.value)}
                />
                <div className="border border-gray-200 rounded max-h-64 overflow-auto">
                  {filteredUsers.length === 0 && (
                    <div className="p-3 text-sm text-gray-500 italic">Brak pasujących osób</div>
                  )}
                  {filteredUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={memberIds.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                      />
                      <span className="flex-1">{u.full_name || u.username}</span>
                      {u.role && <span className="text-xs text-gray-400">{u.role}</span>}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                  onClick={cancelEdit}
                  disabled={busy}
                >
                  Anuluj
                </button>
                <button
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {busy ? 'Zapisywanie…' : 'Zapisz'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string, children: React.ReactNode, className?: string }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-gray-700 font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
